import { z } from 'zod'

import { DateZod } from '../common'

import {
  SUPPORT_DIAGNOSTIC_MAX_LENGTH,
  SUPPORT_PROBABLE_CAUSE_MAX_LENGTH,
  SUPPORT_TEXT_MAX_LENGTH,
  SupportAuthorZod,
  SupportLinearTicketZod,
  SupportLineZod,
  SupportPersonZod,
  SupportStatusZod,
} from './support-request-zod'

export const SupportAudienceZod = z.enum(['requester', 'agents'])
export type SupportAudience = z.infer<typeof SupportAudienceZod>

export const SupportActorZod = SupportAuthorZod.extend({
  role: z.enum(['requester', 'l1', 'l2']),
})
export type SupportActor = z.infer<typeof SupportActorZod>

export const SupportMessageItemZod = z.object({
  kind: z.literal('message'),
  audience: z.literal('requester'),
  createdAt: DateZod,
  from: z.enum(['requester', 'escouade']),
  author: SupportAuthorZod,
  text: z.string().max(SUPPORT_TEXT_MAX_LENGTH),
  opening: z.boolean().nullish().default(false),
})
export type SupportMessageItem = z.infer<typeof SupportMessageItemZod>

export const SupportNoteItemZod = z.object({
  kind: z.literal('note'),
  audience: z.literal('agents'),
  createdAt: DateZod,
  line: SupportLineZod,
  author: SupportAuthorZod,
  text: z.string().max(SUPPORT_TEXT_MAX_LENGTH),
  statusKept: z.boolean(),
})
export type SupportNoteItem = z.infer<typeof SupportNoteItemZod>

export const SupportDiagnosticItemZod = z.object({
  kind: z.literal('diagnostic'),
  audience: z.literal('agents'),
  createdAt: DateZod,
  producedAt: DateZod,
  line: z.literal('l2'),
  author: SupportAuthorZod,
  markdown: z.string().min(1).max(SUPPORT_DIAGNOSTIC_MAX_LENGTH),
  probableCause: z.string().min(1).max(SUPPORT_PROBABLE_CAUSE_MAX_LENGTH),
})
export type SupportDiagnosticItem = z.infer<typeof SupportDiagnosticItemZod>

export const SupportAgentEventTypeZod = z.enum(['taken', 'assigned', 'unassigned', 'status', 'linked'])
export const SupportRequesterEventTypeZod = z.enum([
  'taken',
  'waitingRequester',
  'waitingEscouade',
  'reopened',
  'settled',
])

export const SupportAgentEventItemZod = z.object({
  kind: z.literal('event'),
  audience: z.literal('agents'),
  createdAt: DateZod,
  type: SupportAgentEventTypeZod,
  actor: SupportActorZod,
  assignee: SupportPersonZod.nullish(),
  from: SupportStatusZod.nullish(),
  to: SupportStatusZod.nullish(),
  ticket: SupportLinearTicketZod.nullish(),
  replaces: z.string().nullish(),
  diagnosticAt: DateZod.nullish(),
})
export type SupportAgentEventItem = z.infer<typeof SupportAgentEventItemZod>

export const SupportRequesterEventItemZod = z.object({
  kind: z.literal('event'),
  audience: z.literal('requester'),
  createdAt: DateZod,
  type: SupportRequesterEventTypeZod,
  // Only a settlement names who did it: the central must never learn which agent, VALIAN above all, moved the request.
  actor: SupportActorZod.nullish(),
  assignee: SupportPersonZod.nullish(),
})
export type SupportRequesterEventItem = z.infer<typeof SupportRequesterEventItemZod>

export const SupportEventItemZod = z.union([SupportAgentEventItemZod, SupportRequesterEventItemZod])
export type SupportEventItem = z.infer<typeof SupportEventItemZod>

export const SupportRequestItemZod = z.union([
  SupportMessageItemZod,
  SupportNoteItemZod,
  SupportDiagnosticItemZod,
  SupportAgentEventItemZod,
  SupportRequesterEventItemZod,
])
export type SupportRequestItem = z.infer<typeof SupportRequestItemZod>
